import { useCallback, useMemo, useState } from 'react';
import { Box, Button, IconButton, Tooltip, useTheme } from '@mui/material';
import {
  GridColDef,
  GridPaginationModel,
  GridRowModel,
} from '@mui/x-data-grid';
import _ from 'lodash';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_ADMIN_ITEM_COUNT_PER_PAGE } from '@/config/constants';
import { useGetAgentsByAdminQuery } from '@/apis/extended/admin/agentApi';
import {
  useGetProductsByAdminQuery,
  useUpdateProductByAdminMutation,
} from '@/apis/extended/admin/productApi';
import { useRemoveProductMutation } from '@/apis/extended/productApi';
import { CommonEditIconButton } from '@/components/Common/CommonButton/CommonEditIconButton';
import { CommonRemoveIconButton } from '@/components/Common/CommonButton/CommonRemoveIconButton';
import { CommonDataGrid } from '@/components/Common/CommonDataGrid';
import { DataGridIconCell } from '@/components/Common/CommonDataGrid/DataGridIconCell';
import { DataGridSelect } from '@/components/Common/CommonDataGrid/DataGridSelect';
import { DataGridTextField } from '@/components/Common/CommonDataGrid/DataGridTextField';
import { CommonImage } from '@/components/Common/CommonImage';
import { CreateProductModal } from '@/components/ConfirmModals/CreateProductModal';
import { RemoveProductModal } from '@/components/ConfirmModals/RemoveProductModal';
import { Loader } from '@/components/Layout/Loader';
import { useServerSort } from '@/hooks/useServerSort';
import { formatPrice } from '@/utils/helper';
import { Product } from '@/types/ProductType';

export const ManageProductTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_ADMIN_ITEM_COUNT_PER_PAGE);
  const { sortParams, gridSortProps } = useServerSort(() => setOffset(0));
  const [removingProduct, setRemovingProduct] = useState<Product | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const isRemoveProductModalOpen = Boolean(removingProduct);

  const { data, isLoading, isFetching } = useGetProductsByAdminQuery({
    offset,
    limit,
    ...sortParams,
  });
  const { data: agentsData } = useGetAgentsByAdminQuery({
    limit: 1000,
    offset: 0,
  });
  const [updateProductByAdmin] = useUpdateProductByAdminMutation();
  const [removeProductByAdmin] = useRemoveProductMutation();

  const columns = useMemo(
    () =>
      [
        {
          field: 'photo',
          sortable: false,
          headerName: '',
          width: 150,
          renderCell: (params) => {
            return (
              <Box
                sx={{
                  width: '100%',
                  height: 'auto',
                  aspectRatio: '4 / 3',
                  borderRadius: '8px',
                  my: 1,
                  border: '1px solid',
                  borderColor: theme.palette.mainColors.primary,
                  overflow: 'hidden',
                }}
              >
                <CommonImage photo={params.value} />
              </Box>
            );
          },
        },
        {
          field: 'name',
          headerName: 'Name',
          minWidth: 300,
          flex: 1,
          editable: true,
          renderEditCell: (params) => {
            return <DataGridTextField {...params} />;
          },
        },
        {
          field: 'price',
          headerName: 'Price',
          width: 120,
          editable: true,
          renderCell: (params) => {
            return `${formatPrice(params.value)}`;
          },
          renderEditCell: (params) => {
            return <DataGridTextField {...params} type="number" />;
          },
        },
        {
          field: 'user',
          sortable: false,
          headerName: 'Owner',
          width: 180,
          renderCell: (params) => {
            return params.value?.email;
          },
        },
        {
          field: 'agent',
          sortable: false,
          headerName: 'Agent',
          width: 200,
          editable: true,
          renderCell: (params) => {
            return params.value?.name || params.value?.email || 'Not Assigned';
          },
          renderEditCell: (params) => {
            const agentOptions = [
              { value: '', label: 'Not Assigned' },
              ...(agentsData?.agents || []).map((agent: any) => ({
                value: agent._id,
                label: agent.name || agent.email || agent._id,
              })),
            ];
            return <DataGridSelect {...params} items={agentOptions} />;
          },
          valueGetter: (params: any) => {
            if (!params || !params.row) return '';
            return params.row.agent?._id || params.row.agent || '';
          },
        },
        {
          field: 'contract',
          sortable: false,
          headerName: 'Contract',
          width: 80,
          renderCell: (params) => {
            return (
              <DataGridIconCell
                status={Boolean(params.value)}
                icon="fa-badge-check"
              />
            );
          },
        },
        {
          field: 'actions',
          headerName: 'Actions',
          type: 'actions',
          getActions: (params) => [
            <Tooltip title="Preview as User">
              <IconButton
                key="preview"
                size="small"
                onClick={() =>
                  window.open(
                    `/estimate/${params.id}`,
                    '_blank',
                    'noopener,noreferrer'
                  )
                }
              >
                <i className="fa-light fa-eye" />
              </IconButton>
            </Tooltip>,
            <CommonEditIconButton
              key="edit"
              onClick={() => navigate(`/admin/product/${params.id}`)}
            />,
            <CommonRemoveIconButton
              key="remove"
              onClick={() => setRemovingProduct(params.row)}
            />,
          ],
        },
      ] as GridColDef<Product>[],
    []
  );

  const onPaginationChanged = ({ page, pageSize }: GridPaginationModel) => {
    setOffset(page * pageSize);
    setLimit(pageSize);
  };

  const processRowUpdate = useCallback(
    (newRow: GridRowModel, oldRow: GridRowModel) =>
      new Promise<GridRowModel>((resolve, reject) => {
        const isUpdated = !_.isEqual(newRow, oldRow);
        if (isUpdated) {
          updateProductByAdmin({
            id: newRow._id,
            ..._.pick(newRow, ['name', 'price', 'agent']),
          })
            .unwrap()
            .then(() => resolve(newRow))
            .catch(() => resolve(oldRow));
        } else {
          resolve(oldRow);
        }
      }),
    [updateProductByAdmin]
  );

  const handleRemoveProduct = () => {
    if (!removingProduct) return;
    removeProductByAdmin(removingProduct._id);
    setRemovingProduct(null);
  };

  if (isLoading || !data) return <Loader isFull />;

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<i className="fa-light fa-plus" />}
          onClick={() => setIsCreateModalOpen(true)}
        >
          Add Property
        </Button>
      </Box>
      <CommonDataGrid
        columns={columns}
        rows={data.products}
        rowCount={data.count}
        getRowHeight={() => 'auto'}
        paginationModel={{ page: offset / limit, pageSize: limit }}
        onPaginationModelChange={onPaginationChanged}
        {...gridSortProps}
        loading={isFetching}
        processRowUpdate={processRowUpdate}
      />
      <RemoveProductModal
        isOpen={isRemoveProductModalOpen}
        onClose={() => setRemovingProduct(null)}
        onConfirm={handleRemoveProduct}
      />
      <CreateProductModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />
    </>
  );
};
